import { AddOrEditDataSourceRequest } from '@upright/common/model/input/data-sources/api-types';
import { DataSourceNameById } from '@upright/common/model/input/data-sources/types';
import { keyBy, mapValues } from 'lodash';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { addDataSource, getAccessibleDataSources, getAllDataSources, getPublicDataSources } from '.';
import { useIsTeam } from '../../../../common/generic/hooks';

export function useGetAllDataSources() {
  const isTeam = useIsTeam();
  return useQuery({
    queryKey: ['getAllDataSources'],
    queryFn: getAllDataSources,
    enabled: isTeam, // Data sources are only available for team users
  });
}

export function useGetAccessibleDataSourceNames() {
  const isTeam = useIsTeam();
  return useQuery<DataSourceNameById, Error>({
    queryKey: ['getAccessibleDataSources'],
    queryFn: async () => {
      const sources = await getAccessibleDataSources();
      const sourcesById = keyBy(sources, s => s.id);
      const sourceNamesById = mapValues(sourcesById, s => s.name);
      return sourceNamesById;
    },
    staleTime: isTeam ? 60_000 : Infinity,
  });
}

export function useGetPublicDataSources() {
  return useQuery({
    queryKey: ['getPublicDataSources'],
    queryFn: getPublicDataSources,
  });
}

export function useUpsertDataSources() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AddOrEditDataSourceRequest) => {
      await addDataSource(payload);
    },

    onSettled: () => {
      void queryClient.invalidateQueries({
        queryKey: ['getAllDataSources'],
      });
      void queryClient.invalidateQueries({
        queryKey: ['getPublicDataSources'],
      });
    },
  });
}
